import React from 'react'
import moment from 'moment'
import Button from '@mui/material/Button'
import Card from '@mui/material/Card'
import Chip from '@mui/material/Chip'
import InputAdornment from '@mui/material/InputAdornment'
import Stack from '@mui/material/Stack'
import TextField from '@mui/material/TextField'
import RestartAltIcon from '@mui/icons-material/RestartAlt'
import SearchIcon from '@mui/icons-material/Search'
import AnDateField from '../../../../components/DateTime/DateField'
import EmployeeSelector from '../../../events/EmployeeSelector'
import { FilterGroup, ToggleChip } from '../SearchBar'
import { FILTERABLE_KINDS, kindMeta } from './accessLogPresentation'

export const DATE_PRESETS = [
  {
    label: 'Today',
    range: () => [moment().startOf('day'), moment().endOf('day')],
  },
  {
    label: 'Past 7 Days',
    range: () => [
      moment().subtract(7, 'days').startOf('day'),
      moment().endOf('day'),
    ],
  },
  {
    label: 'Past 30 Days',
    range: () => [
      moment().subtract(30, 'days').startOf('day'),
      moment().endOf('day'),
    ],
  },
  { label: 'All Dates', range: () => [null, null] },
]

export const defaultAccessLogFilters = () => ({
  search: '',
  startDate: moment().subtract(7, 'days').startOf('day'),
  endDate: moment().endOf('day'),
  kinds: [],
  accessedBy: null,
  externalOnly: false,
})

const AccessLogFilters = ({ filters, setFilters, onSearch, loading }) => {
  const applyPreset = (range) => {
    const [startDate, endDate] = range()
    setFilters((prev) => ({ ...prev, startDate, endDate }))
    onSearch({ startDate, endDate })
  }

  const activePreset = DATE_PRESETS.find(({ range }) => {
    const [presetStart, presetEnd] = range()
    if (!presetStart && !presetEnd) return !filters.startDate && !filters.endDate
    return (
      filters.startDate &&
      filters.endDate &&
      presetStart.isSame(moment(filters.startDate), 'day') &&
      presetEnd.isSame(moment(filters.endDate), 'day')
    )
  })?.label

  const toggleKind = (kind) =>
    setFilters((prev) => ({
      ...prev,
      kinds: prev.kinds.includes(kind)
        ? prev.kinds.filter((k) => k !== kind)
        : [...prev.kinds, kind],
    }))

  const reset = () => {
    const next = defaultAccessLogFilters()
    setFilters(next)
    onSearch(next)
  }

  return (
    <Card
      variant="outlined"
      component="form"
      onSubmit={(event) => {
        event.preventDefault()
        onSearch()
      }}
      sx={{ p: 1.5 }}
    >
      <Stack gap={1.5}>
        <Stack
          direction={{ xs: 'column', md: 'row' }}
          gap={1.5}
          alignItems={{ md: 'center' }}
        >
          <TextField
            placeholder="Record number or patient surname…"
            size="small"
            value={filters.search ?? ''}
            onChange={(event) =>
              setFilters((prev) => ({ ...prev, search: event.target.value }))
            }
            sx={{ flex: 1, minWidth: 200 }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" />
                </InputAdornment>
              ),
            }}
          />
          <Stack direction="row" gap={1.5} alignItems="center">
            <AnDateField
              fullWidth
              date={filters.startDate}
              size="small"
              clearable
              onChange={(newDate) =>
                setFilters((prev) => ({ ...prev, startDate: newDate }))
              }
              label="From"
            />
            <AnDateField
              fullWidth
              date={filters.endDate}
              size="small"
              clearable
              onChange={(newDate) =>
                setFilters((prev) => ({ ...prev, endDate: newDate }))
              }
              label="To"
            />
          </Stack>
          <EmployeeSelector
            value={filters.accessedBy ?? null}
            onChange={(event, newValue) =>
              setFilters((prev) => ({ ...prev, accessedBy: newValue }))
            }
            label="Accessed by"
            size="small"
            sx={{ flex: 1, minWidth: 220 }}
          />
        </Stack>

        <Stack
          direction={{ xs: 'column', lg: 'row' }}
          gap={{ xs: 1.5, lg: 4 }}
          flexWrap="wrap"
        >
          <FilterGroup label="Activity">
            {FILTERABLE_KINDS.map((kind) => {
              const meta = kindMeta(kind)
              return (
                <ToggleChip
                  key={kind}
                  label={meta.label}
                  color={meta.color === 'default' ? 'anMainBlue' : meta.color}
                  selected={filters.kinds.includes(kind)}
                  onClick={() => toggleKind(kind)}
                />
              )
            })}
          </FilterGroup>
          <FilterGroup label="Who">
            <ToggleChip
              label="External visitors only"
              color="warning"
              selected={!!filters.externalOnly}
              onClick={() =>
                setFilters((prev) => ({ ...prev, externalOnly: !prev.externalOnly }))
              }
            />
          </FilterGroup>
        </Stack>

        <Stack
          direction="row"
          gap={1}
          flexWrap="wrap"
          alignItems="center"
          justifyContent="space-between"
        >
          <Stack direction="row" gap={1} flexWrap="wrap">
            {DATE_PRESETS.map(({ label, range }) => (
              <Chip
                key={label}
                label={label}
                size="small"
                color={activePreset === label ? 'anMainBlue' : 'default'}
                variant={activePreset === label ? 'filled' : 'outlined'}
                onClick={() => applyPreset(range)}
                sx={{ fontWeight: 600 }}
              />
            ))}
          </Stack>
          <Stack direction="row" gap={1}>
            <Button
              size="small"
              color="inherit"
              variant="outlined"
              startIcon={<RestartAltIcon />}
              onClick={reset}
            >
              Reset
            </Button>
            <Button
              variant="contained"
              color="success"
              size="small"
              disabled={loading}
              endIcon={<SearchIcon />}
              type="submit"
            >
              Search
            </Button>
          </Stack>
        </Stack>
      </Stack>
    </Card>
  )
}

export default AccessLogFilters
